import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import {
  buildHomeCarouselTypeIdSets,
  buildOfficeVoiceSearchTypeIdSet,
  catalogLabelMatchesHomeGymCarousel,
  catalogLabelMatchesHomeOfficeCarousel,
  catalogLabelMatchesOfficeVoiceSearch,
  isHomeGymBrowseTypeSelection,
  normalizeAssetTypeName,
} from '../constants/filterableAssetTypes';
import { assetRecordSubTitle } from './assetHomeCardSubtitle';

function coerceNumericField(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Numeric asset type id from list/search rows (varied wire shapes). */
export function assetRecordAssetTypeId(asset: Record<string, unknown>): number | null {
  for (const key of [
    'asset_type_id',
    'assetTypeId',
    'type_id',
    'facility_type_id',
  ] as const) {
    const n = coerceNumericField(asset[key]);
    if (n !== null) {
      return n;
    }
  }
  const nested = asset.asset_type;
  if (isPlainObject(nested)) {
    return (
      coerceNumericField(nested.id) ??
      coerceNumericField(nested.asset_type_id) ??
      coerceNumericField(nested.type_id) ??
      null
    );
  }
  return null;
}

function assetTypeNameBag(asset: Record<string, unknown>): Set<string> {
  const bag = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      bag.add(normalizeAssetTypeName(raw));
    }
  };
  add(asset.asset_type_name);
  add(asset.assetTypeName);
  add(asset.type_name);
  add(asset.type);
  if (typeof asset.asset_type === 'string') {
    add(asset.asset_type);
  }
  if (isPlainObject(asset.asset_type)) {
    add(asset.asset_type.name);
    add(asset.asset_type.label);
    add(asset.asset_type.type_name);
    add(asset.asset_type.title);
  }
  return bag;
}

function labelBagMatchesOffice(names: Set<string>): boolean {
  return Array.from(names).some((name) => catalogLabelMatchesHomeOfficeCarousel(name));
}

function labelBagMatchesGym(names: Set<string>): boolean {
  return Array.from(names).some((name) => catalogLabelMatchesHomeGymCarousel(name));
}

function labelBagMatchesOfficeVoiceSearch(names: Set<string>): boolean {
  return Array.from(names).some((name) => catalogLabelMatchesOfficeVoiceSearch(name));
}

function catalogEntryForTypeId(
  catalog: GeneralInfoPicklistItem[],
  typeId: number,
): GeneralInfoPicklistItem | undefined {
  return catalog.find((item) => item.id === typeId);
}

export function assetMatchesHomeOfficeCarousel(
  asset: Record<string, unknown>,
  catalog: GeneralInfoPicklistItem[],
  officeTypeIds: Set<number>,
): boolean {
  const typeId = assetRecordAssetTypeId(asset);
  if (typeId !== null) {
    if (officeTypeIds.has(typeId)) {
      return true;
    }
    const entry = catalogEntryForTypeId(catalog, typeId);
    if (entry && catalogLabelMatchesHomeOfficeCarousel(entry.label)) {
      return true;
    }
  }
  return labelBagMatchesOffice(assetTypeNameBag(asset));
}

/**
 * Assets list filter for the home “Gym” browse slot — same rules as the home gym carousel.
 */
export function assetMatchesBrowseGymListFilter(
  asset: Record<string, unknown>,
  catalog: GeneralInfoPicklistItem[],
  typeId: number,
  catalogLabelNorm: string | null,
): boolean {
  if (!isHomeGymBrowseTypeSelection(typeId, catalogLabelNorm)) {
    return false;
  }
  const { gymIds } = buildHomeCarouselTypeIdSets(catalog);
  return assetMatchesHomeGymCarousel(asset, catalog, gymIds);
}

const OFFICE_LISTING_WORD_RE = /משרד|office/i;

const OFFICE_LISTING_COPY_KEYS = ['asset_name', 'facility_name', 'name', 'description'] as const;

/** Title / subtitle / description mention office or משרד (not the catalog type label alone). */
export function assetListingCopyHasOfficeWord(asset: Record<string, unknown>): boolean {
  for (const key of OFFICE_LISTING_COPY_KEYS) {
    const raw = asset[key];
    if (typeof raw === 'string' && OFFICE_LISTING_WORD_RE.test(raw)) {
      return true;
    }
  }
  const subtitle = assetRecordSubTitle(asset);
  return Boolean(subtitle && OFFICE_LISTING_WORD_RE.test(subtitle));
}

function assetMatchesOfficeVoiceSearchType(
  asset: Record<string, unknown>,
  catalog: GeneralInfoPicklistItem[],
  officeTypeIds: Set<number>,
): boolean {
  const typeId = assetRecordAssetTypeId(asset);
  if (typeId !== null) {
    if (officeTypeIds.has(typeId)) {
      return true;
    }
    const entry = catalogEntryForTypeId(catalog, typeId);
    if (entry && catalogLabelMatchesOfficeVoiceSearch(entry.label)) {
      return true;
    }
  }
  return labelBagMatchesOfficeVoiceSearch(assetTypeNameBag(asset));
}

/**
 * Spoken office / משרד search: private office and open space always match;
 * lounge and conference room only when listing copy contains משרד / office.
 */
export function assetMatchesOfficeVoiceSearch(
  asset: Record<string, unknown>,
  catalog: GeneralInfoPicklistItem[],
  officeTypeIds: Set<number>,
): boolean {
  if (!assetMatchesOfficeVoiceSearchType(asset, catalog, officeTypeIds)) {
    return false;
  }
  const { officeIds } = buildHomeCarouselTypeIdSets(catalog);
  if (assetMatchesHomeOfficeCarousel(asset, catalog, officeIds)) {
    return true;
  }
  return assetListingCopyHasOfficeWord(asset);
}

export function filterOfficeVoiceSearchAssets(
  assets: Record<string, unknown>[],
  catalog: GeneralInfoPicklistItem[],
): Record<string, unknown>[] {
  const officeIds = buildOfficeVoiceSearchTypeIdSet(catalog);
  return assets.filter((asset) => assetMatchesOfficeVoiceSearch(asset, catalog, officeIds));
}

export function assetMatchesHomeGymCarousel(
  asset: Record<string, unknown>,
  catalog: GeneralInfoPicklistItem[],
  gymTypeIds: Set<number>,
): boolean {
  const typeId = assetRecordAssetTypeId(asset);
  if (typeId !== null) {
    if (gymTypeIds.has(typeId)) {
      return true;
    }
    const entry = catalogEntryForTypeId(catalog, typeId);
    if (entry && catalogLabelMatchesHomeGymCarousel(entry.label)) {
      return true;
    }
  }
  return labelBagMatchesGym(assetTypeNameBag(asset));
}

/** Keep only gym / fitness carousel types. */
export function filterHomeGymCarouselAssets(
  assets: Record<string, unknown>[],
  catalog: GeneralInfoPicklistItem[],
): Record<string, unknown>[] {
  const { gymIds } = buildHomeCarouselTypeIdSets(catalog);
  return assets.filter((asset) => assetMatchesHomeGymCarousel(asset, catalog, gymIds));
}

/** Drop gym / fitness types from mixed home carousels (recommended, vicinity). */
export function excludeHomeGymCarouselAssets(
  assets: Record<string, unknown>[],
  catalog: GeneralInfoPicklistItem[],
): Record<string, unknown>[] {
  const { gymIds } = buildHomeCarouselTypeIdSets(catalog);
  return assets.filter((asset) => !assetMatchesHomeGymCarousel(asset, catalog, gymIds));
}
